import {
  addDays,
  personalDateOf,
  planReminders,
  reminderSlots,
  resolveCheckinLogs,
  unfireReminders,
  type CheckinDayLog,
  type ReminderItem,
  type ReminderPlan,
  type ReminderSettings,
  type ReminderState,
} from '@wiztime/core';

/** Longest wait between two checks: a woken computer or a changed setting is noticed within it. */
export const REMINDER_CHECK_MS = 60_000;
const MIN_WAIT_MS = 1_000;

/** What one check reads from storage. */
export interface ReminderSnapshot {
  readonly settings: ReminderSettings;
  /** Live check-in items. */
  readonly items: readonly ReminderItem[];
  /**
   * Check-in logs that can belong to occurrences of the given dates, ticked or not, with their
   * HLC: of a legacy and a new log of one occurrence the newer decides (data dictionary §3.2).
   */
  readonly logs: readonly CheckinDayLog[];
}

export interface ReminderRunnerDeps {
  readonly now: () => number;
  readonly loadSettings: () => Promise<ReminderSettings>;
  /** Items and the check-in logs of `dates`. */
  readonly load: (dates: readonly string[]) => Promise<Omit<ReminderSnapshot, 'settings'>>;
  readonly readState: () => ReminderState;
  readonly writeState: (state: ReminderState) => void;
  /** Show the plan's notifications (not called when there is nothing to show); resolves to the keys that could not be shown. */
  readonly notify: (plan: ReminderPlan, now: number) => Promise<readonly string[]>;
  /**
   * Run `body` while no other tab of this browser runs a reminder check or snooze, so the shared
   * state is read and written by one tab at a time and a reminder is shown once.
   */
  readonly withLock: (body: () => Promise<void>) => Promise<void>;
  readonly setTimer: (handler: () => void, ms: number) => number;
  readonly clearTimer: (handle: number) => void;
}

export interface ReminderRunner {
  start(): void;
  stop(): void;
  /** Check now (page shown again, a snooze, a setting change); re-arms the timer. */
  check(): Promise<void>;
}

/**
 * Plan for `now` from storage: yesterday's (a postponed reminder across the day start), today's
 * and tomorrow's (a lead can reach across the day start) personal dates.
 */
export async function planAt(
  deps: Pick<ReminderRunnerDeps, 'loadSettings' | 'load' | 'readState'>,
  now: number,
): Promise<ReminderPlan> {
  const settings = await deps.loadSettings();
  const today = personalDateOf(now, settings.dayStartMin);
  const dates = [addDays(today, -1), today, addDays(today, 1)];
  const { items, logs } = await deps.load(dates);
  const resolved = resolveCheckinLogs(items, logs, settings.legacyDayStartMin);
  return planReminders({
    slots: reminderSlots(items, dates, settings),
    // Day-list keys of timed occurrences are reminder keys (`{itemId}|{occursAt}`).
    doneKeys: new Set([...resolved].flatMap(([key, log]) => (log.done ? [key] : []))),
    state: deps.readState(),
    now,
    today,
  });
}

/**
 * The reminder loop of one page: check, show what is due, sleep until the next reminder (at most
 * a minute, since background tabs throttle timers and the data may change meanwhile). Checks never
 * overlap; a failed read is retried at the next check.
 */
export function createReminderRunner(deps: ReminderRunnerDeps): ReminderRunner {
  let timer: number | null = null;
  let running = false;
  let checking: Promise<void> | null = null;

  const arm = (nextAt: number | null): void => {
    if (!running) return;
    if (timer !== null) deps.clearTimer(timer);
    const wait = nextAt === null ? REMINDER_CHECK_MS : nextAt - deps.now();
    timer = deps.setTimer(() => void check(), Math.min(REMINDER_CHECK_MS, Math.max(MIN_WAIT_MS, wait)));
  };

  const run = async (): Promise<void> => {
    let nextAt: number | null = null;
    try {
      await deps.withLock(async () => {
        const now = deps.now();
        const previous = deps.readState();
        const plan = await planAt({ ...deps, readState: () => previous }, now);
        if (!running) return;
        const failed =
          plan.onTime.length > 0 || plan.missed.length > 0 ? await deps.notify(plan, now) : [];
        deps.writeState(unfireReminders(plan.state, previous, failed));
        nextAt = plan.nextAt;
      });
    } catch (error) {
      console.warn('[reminder] check failed; retrying at the next check', error);
    }
    arm(nextAt);
  };

  const check = (): Promise<void> => {
    if (!running) return Promise.resolve();
    checking ??= run().finally(() => {
      checking = null;
    });
    return checking;
  };

  return {
    start() {
      if (running) return;
      running = true;
      void check();
    },
    stop() {
      running = false;
      if (timer !== null) deps.clearTimer(timer);
      timer = null;
    },
    check,
  };
}
